import React, { useCallback, useEffect } from 'react';

import _debounce from 'lodash/debounce';
import { useForm, useFormState } from 'react-final-form';
import { useSelector, useStore } from 'react-redux';

import { checkEmailAvailabilityAction } from 'api/CompeonReverseApi/customer/actions';
import FormRow from 'components/FormRow';
import FormSection from 'components/FormSection';
import { GenderWithField } from 'components/GenderButtons/GenderButtons';
import { InputWithField } from 'components/Input';
import LogoutButton from 'components/LogoutButton';
import StaticField from 'components/StaticField';
import { ContactPersonEmail } from 'new/contactPerson/ContactPersonEmail';
import { translations } from 'new/form/common/types';
import { getSectionValidations } from 'new/form/progress/useCalculateProgress';
import {
  useFormValidations,
  useMode,
  useInquiryProcessConfig,
} from 'new/form/state/inquiryProcessConfig/hooks';
import { PageStateActionTypes } from 'new/form/state/pageStateReducer';
import { useToasts } from 'shared/hooks/useToasts';
import { store } from 'store';
import { setIsEmailAvailable } from 'store/emailAvailable/actions';
import {
  isEmailAvailableSelector,
  isLoadingEmailAvailabilitySelector,
} from 'store/emailAvailable/selectors';
import PhoneNumberField from 'ui/common/phoneNumberField';
import useDispatchApiCall from 'utils/hooks/useDispatchApiCallHook';
import { useTranslations } from 'utils/hooks/useTranslations';
import { isEmailRegex } from 'utils/regexes';
import { getCurrentUser } from 'utils/user/getters';

import { mittweidaFields, MittweidaFieldTypes } from '../../../../fields';

// This is a workaround until we add react-query. With react-query we can pass the query client to our validation functions
// and execute async queries inside our ValidationFunctions to avoid all of these hacky async redux thingies.
function useCheckEmailValidity() {
  const email =
    useFormState<MittweidaFieldTypes>()?.values?.userProfilePage?.contactPersonSection?.email;
  const { makeCall } = useDispatchApiCall({
    showErrorNotification: false,
    errorMessage: '',
    isPendingInitially: false,
  });
  const {
    state,
    dispatch,
    formStateData: { conditions },
  } = useInquiryProcessConfig();
  const validations = useFormValidations();
  const validationsForCurrentStep = validations[state.currentStep];
  const { values } = useFormState<MittweidaFieldTypes>();
  const form = useForm();
  const store = useStore();

  const isEmailAvailable = useSelector(isEmailAvailableSelector);
  const isEmailLoading = useSelector(isLoadingEmailAvailabilitySelector);

  //eslint-disable-next-line
  const checkEmailDebounce = useCallback(
    _debounce(async (email: string | undefined) => {
      if (email) {
        const isValidEmail = isEmailRegex.test(email);
        if (isValidEmail) {
          const result = await makeCall(checkEmailAvailabilityAction(email));
          if (result.payload?.status === 200) {
            setIsEmailAvailable(true);
          } else {
            setIsEmailAvailable(false);
          }
        }
      }
    }, 200),
    [],
  );

  useEffect(() => {
    checkEmailDebounce(email);
  }, [email, checkEmailDebounce]);

  useEffect(() => {
    if (!isEmailLoading) {
      const sectionValidations = getSectionValidations(
        validationsForCurrentStep,
        form,
        values,
        store,
        conditions,
        state.currentStep as string,
      );

      dispatch({
        type: PageStateActionTypes.SET_PROGRESS,
        payload: {
          ...state.pageValidations,
          [state.currentStep]: {
            ...sectionValidations,
          },
        },
      });
    }
    //eslint-disable-next-line
  }, [isEmailAvailable, isEmailLoading, dispatch]);
}

const ContactPersonFormSection = ({ children }: { children: React.ReactNode }) => {
  const t = useTranslations();

  return (
    <FormSection
      title={t(translations.inquiryType.mittweida.pages.userDetails.sections.contactPerson.title)}
      name={'contactPerson'}
      sectionNumber={1}
    >
      {children}
    </FormSection>
  );
};

export const ContactPersonSection = () => {
  const { gender, firstName, lastName, email, phone } =
    mittweidaFields.userProfilePage.contactPersonSection;
  useCheckEmailValidity();

  return (
    <ContactPersonFormSection>
      <FormRow>
        <GenderWithField name={gender} />
        <InputWithField name={firstName} />
        <InputWithField name={lastName} />
      </FormRow>
      <FormRow separator>
        <ContactPersonEmail name={email} />
      </FormRow>
      <FormRow>
        <PhoneNumberField name={phone} />
      </FormRow>
    </ContactPersonFormSection>
  );
};

export const LoggedInContactPersonSection = () => {
  let user = getCurrentUser(store.getState());
  const t = useTranslations();
  const mode = useMode();
  const { error } = useToasts();

  const { userProfileName, userProfileEmail } =
    translations.pages.userProfile.sections.completingContactData.fields;

  if (!user) {
    error({ description: t('errors.actionFailed') });
  }

  user = user as { name: string; email: string };
  return (
    <ContactPersonFormSection>
      <FormRow alignItems={'flex-start'}>
        <StaticField caption={t(userProfileName.caption)} text={user.name} />
        <StaticField caption={t(userProfileEmail.caption)} text={user.email} />
        {mode === 'create' ? <LogoutButton /> : null}
      </FormRow>
    </ContactPersonFormSection>
  );
};
